import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OpenAPI } from 'app/API';
import { useState } from 'react';
import { EndpointCard } from '@pages/Admin/ApiDocsPages/components/EndpointCard';
import { EndpointSection } from '@pages/Admin/ApiDocsPages/components/EndpointSection';
import { MethodBadgeGet } from '@pages/Admin/ApiDocsPages/components/MethodBadgeGet';
import { MethodBadgePost } from '@pages/Admin/ApiDocsPages/components/MethodBadgePost';
import { ParamRow } from '@pages/Admin/ApiDocsPages/components/ParamRow';
import { ParamsTable } from '@pages/Admin/ApiDocsPages/components/ParamsTable';
import { TApiEndpoint, TApiField } from '../../endpoints';

type TProps = {
  endpoint: TApiEndpoint;
  apiKey: string;
  orgId: string;
};

type TResult = { status: number; ok: boolean; body: string };

const buildInitialValues = (fields: TApiField[], orgId: string): Record<string, string> =>
  fields.reduce<Record<string, string>>((acc, field) => {
    acc[field.name] = field.name === 'oid' ? orgId : '';
    return acc;
  }, {});

export const EndpointTester = ({ endpoint, apiKey, orgId }: TProps) => {
  const [open, setOpen] = useState(false);
  const [pathValues, setPathValues] = useState<Record<string, string>>(() =>
    buildInitialValues(endpoint.pathParams, orgId),
  );
  const [queryValues, setQueryValues] = useState<Record<string, string>>(() =>
    buildInitialValues(endpoint.queryParams ?? [], orgId),
  );
  const [bodyText, setBodyText] = useState(endpoint.bodyExample ?? '');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<TResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const hasBody = endpoint.method === 'POST' && !!endpoint.bodyFields?.length;

  const buildUrl = (): string => {
    let path = endpoint.path;
    endpoint.pathParams.forEach((param) => {
      path = path.replace(`{${param.name}}`, encodeURIComponent(pathValues[param.name] ?? ''));
    });
    const query = (endpoint.queryParams ?? [])
      .filter((param) => (queryValues[param.name] ?? '').length > 0)
      .map((param) => `${encodeURIComponent(param.name)}=${encodeURIComponent(queryValues[param.name])}`)
      .join('&');
    return `${OpenAPI.BASE}${path}${query ? `?${query}` : ''}`;
  };

  const send = async () => {
    setError(null);
    setResult(null);

    const missing = endpoint.pathParams
      .filter((param) => param.required && !(pathValues[param.name] ?? '').trim())
      .map((param) => param.name);
    if (missing.length) {
      setError(`Missing required path parameter(s): ${missing.join(', ')}`);
      return;
    }

    let body: string | undefined;
    if (hasBody && bodyText.trim()) {
      try {
        JSON.parse(bodyText);
        body = bodyText;
      } catch {
        setError('Request body is not valid JSON.');
        return;
      }
    }

    setSending(true);
    try {
      const response = await fetch(buildUrl(), {
        method: endpoint.method,
        headers: {
          'api-key': apiKey,
          ...(body ? { 'Content-Type': 'application/json' } : {}),
        },
        body,
      });
      const text = await response.text();
      let pretty = text;
      try {
        pretty = JSON.stringify(JSON.parse(text), null, 2);
      } catch {
        pretty = text;
      }
      setResult({ status: response.status, ok: response.ok, body: pretty || '(empty response)' });
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Request failed.');
    } finally {
      setSending(false);
    }
  };

  const inputClassName =
    'w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 font-mono text-xs text-[var(--color-text)]';

  return (
    <EndpointCard
      method={endpoint.method === 'GET' ? <MethodBadgeGet /> : <MethodBadgePost />}
      path={endpoint.path}
      summary={endpoint.summary}
    >
      {endpoint.description ? <p>{endpoint.description}</p> : null}

      {endpoint.mutates ? (
        <div className="rounded-md bg-[var(--color-warning-bg)] px-3 py-2 text-xs text-[var(--color-warning-text)]">
          ⚠ This endpoint <strong>changes data</strong> in your organization. Sending it from here performs the real
          action (for example, creating or resubmitting a job).
        </div>
      ) : null}

      {endpoint.pathParams.length ? (
        <EndpointSection title="Path parameters">
          <ParamsTable>
            {endpoint.pathParams.map((param) => (
              <ParamRow
                key={param.name}
                name={param.name}
                type={param.type}
                required={param.required}
                description={param.description}
              />
            ))}
          </ParamsTable>
        </EndpointSection>
      ) : null}

      {endpoint.queryParams?.length ? (
        <EndpointSection title="Query parameters">
          <ParamsTable>
            {endpoint.queryParams.map((param) => (
              <ParamRow
                key={param.name}
                name={param.name}
                type={param.type}
                required={param.required}
                description={param.description}
              />
            ))}
          </ParamsTable>
        </EndpointSection>
      ) : null}

      {endpoint.bodyFields?.length ? (
        <EndpointSection title="Request body">
          <ParamsTable>
            {endpoint.bodyFields.map((param) => (
              <ParamRow
                key={param.name}
                name={param.name}
                type={param.type}
                required={param.required}
                description={param.description}
              />
            ))}
          </ParamsTable>
        </EndpointSection>
      ) : null}

      <EndpointSection title="Responses">
        <ParamsTable nameLabel="Status" typeLabel="Body">
          {endpoint.responses.map((response) => (
            <ParamRow
              key={response.status}
              name={response.status}
              type={response.body}
              description={response.description}
            />
          ))}
        </ParamsTable>
      </EndpointSection>

      <div>
        <button
          type="button"
          className="text-xs font-semibold text-[var(--color-accent)] hover:underline"
          onClick={() => setOpen((value) => !value)}
        >
          {open ? 'Hide' : 'Try it out'}
        </button>
      </div>

      {open ? (
        <div className="flex flex-col gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-3 py-3">
          {endpoint.pathParams.map((param) => (
            <label key={param.name} className="flex flex-col gap-1 text-xs text-[var(--color-text-subtle)]">
              {param.name}
              {param.required ? <span className="text-[var(--color-danger)]"> *</span> : null}
              <input
                className={inputClassName}
                value={pathValues[param.name] ?? ''}
                onChange={(event) => setPathValues((prev) => ({ ...prev, [param.name]: event.target.value }))}
                placeholder={param.name}
              />
            </label>
          ))}

          {(endpoint.queryParams ?? []).map((param) => (
            <label key={param.name} className="flex flex-col gap-1 text-xs text-[var(--color-text-subtle)]">
              {param.name} (query)
              <input
                className={inputClassName}
                value={queryValues[param.name] ?? ''}
                onChange={(event) => setQueryValues((prev) => ({ ...prev, [param.name]: event.target.value }))}
                placeholder={param.name}
              />
            </label>
          ))}

          {hasBody ? (
            <label className="flex flex-col gap-1 text-xs text-[var(--color-text-subtle)]">
              Request body (JSON)
              <textarea
                className={`${inputClassName} min-h-[120px]`}
                value={bodyText}
                onChange={(event) => setBodyText(event.target.value)}
              />
            </label>
          ) : null}

          <div className="flex items-center gap-3">
            <div className="w-[120px]">
              <Button disabled={sending || !apiKey} onClick={send}>
                {sending ? 'Sending…' : 'Send'}
              </Button>
            </div>
            {!apiKey ? (
              <span className="text-xs text-[var(--color-text-subtle)]">Select an API key above first.</span>
            ) : null}
          </div>

          {error ? <p className="text-xs text-[var(--color-danger)]">{error}</p> : null}

          {result ? (
            <div className="flex flex-col gap-1">
              <span
                className={`text-xs font-semibold ${result.ok ? 'text-[var(--color-success-text)]' : 'text-[var(--color-danger)]'}`}
              >
                {result.status} {result.ok ? 'OK' : 'Error'}
              </span>
              <pre className="max-h-[320px] overflow-auto rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 font-mono text-xs text-[var(--color-text)]">
                {result.body}
              </pre>
            </div>
          ) : null}
        </div>
      ) : null}
    </EndpointCard>
  );
};
